import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import StopIcon from '@mui/icons-material/Stop';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LC, r } from '../landing/landingTokens';
import {
  CURATED_GOALS,
  LONG_TAIL_GOALS,
  LOGGED_OUT_GOALS,
  SHORT_LABELS,
} from './curatedHealthGoals';
import { logAnalyticsEvent } from '../../utils/analytics';
import { useAuth } from '../../AuthContext';
import config from '../../config';
import { getGuestMedications } from '../../utils/guestMedications';
import GuestMedicationsControl from '../guestMedications/GuestMedicationsControl';
import { GoalChevron, GoalSuggestions, matchPreset, useGoalCombobox } from './GoalCombobox';

// ─── Rotating example goals ──────────────────────────────────────────────────
// Cycled through the placeholder as `e.g. "..."` to demonstrate the natural-
// language phrasing the terse chips don't convey. Kept short so the full
// `e.g. "..."` string fits one line at 375px (the input doesn't grow for a
// placeholder, so a long example would clip on mobile).
const EXAMPLE_GOALS = [
  'Wake up refreshed',
  'More energy all day',
  'Sharper focus at work',
  'Feel calmer daily',
  'A stronger heart',
  'Ease joint stiffness',
  'Thicker, fuller hair',
  'Stronger bones',
  'Better immunity',
  'Balanced hormones',
];

// ─── Token aliases (stable references, no useTheme needed) ───────────────────
// Green system (landing redesign). Shared with /create-stack — both surfaces
// adopt the green accent intentionally. See docs/landing-page-redesign.
const T = {
  surface:    LC.surface,                   // clean white canvas — pops on the green hero
  stroke:     LC.border,                    // hairline edge on white
  strokeFocus:LC.accent,
  ink:        LC.ink,
  // Circular submit button — solid signal accent with dark ink (brand brief §3).
  submitBg:   LC.accent,
  submitHover:LC.accentHover,
  submitShadow:`0 0.125rem 0.5rem ${alpha(LC.accent, 0.3)}`,
  chipIdle:   LC.surface,                   // near-white pill with hairline border
  chipBorder: LC.border,
  chipSelected: LC.evergreen,               // deep green when chosen
  chipSelectedInk: 'primary.contrastText',
  chipHover:  alpha(LC.accent, 0.1),
  finePrint:  LC.inkMuted,
  muted:      LC.inkSoft,
};

/**
 * StackComposer — chat-UI-inspired health goal input widget.
 *
 * Props:
 *   healthGoals   string        Controlled value of the goal input.
 *   setHealthGoals (v) => void  Setter.
 *   onSubmit      () => void    Called when the user submits (icon click or Enter).
 *   onCancel      () => void    Called when stop icon is clicked during loading.
 *   loading       bool          When true, shows progress bar and stop icon.
 *   surface       'landing' | 'app' | 'analyze_stack'
 *                               'landing' adds "first two stacks are free" microcopy.
 *                               Everything else behaves as 'app'; the value is
 *                               also the label on this widget's own analytics,
 *                               which is how Analyze Current Stack separates
 *                               its composer events from /create-stack's.
 *   ownSubmit     bool          Whether the box submits on its own (default
 *                               true): the arrow, Enter, and one-tap chips.
 *                               False on Analyze Current Stack (handoff,
 *                               2026-09-24), whose one action is the page's
 *                               footer button — a chip there fills the goal,
 *                               and picking it again clears it. A prop rather
 *                               than a `surface` test, because `surface` is an
 *                               analytics label and a rename must not quietly
 *                               change behaviour.
 *   showHeader    bool          Whether to render the headline + sub (default true).
 *   submitLabel   string        Accessible name of the submit control. The
 *                               field is shared with surfaces that do not
 *                               create a stack, and a screen-reader user told
 *                               "Create my stack" on a page that analyses one
 *                               has been given the wrong verb.
 */
export default function StackComposer({
  healthGoals = '',
  setHealthGoals,
  onSubmit,
  onCancel,
  loading = false,
  surface = 'app',
  showHeader = true,
  submitLabel = 'Create my stack',
  ownSubmit = true,
}) {
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  // Drives the resting caret (see below). A ref won't do — this has to re-render.
  const [inputFocused, setInputFocused] = useState(false);
  // Mobile: four goals + "More goals", no hidden scroll. The full labels are
  // too wide for a 390px rail, so chips display the mock's short mobile labels
  // ("Sleep", "Heart") while still submitting the full curated goal string.
  // Remaining goals fold into the explicit "More goals" menu. Derived from the
  // theme's own `sm` breakpoint (not a hardcoded 599px) so this stays in sync
  // with the chip sx below, which uses the same `{ xs, sm }` breakpoint keys.
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  // Logged-out visitors get the short fixed rail with no overflow menu. Only a
  // *positively* known logged-out visitor does — and if this ever renders
  // outside AuthProvider we fall back to the current row.
  // Read off `auth` rather than destructured: the bare `loading` name is
  // already taken by the submit-in-progress prop above.
  const auth = useAuth();
  const showReducedGoals = Boolean(auth) && !auth.loading && !auth.currentUser;
  // While auth resolves, NEITHER rail is safe to show: the logged-in row would
  // flash the full goal list — "More goals" included — at exactly the visitor
  // this rail exists to shorten. `/` never reaches this (App.js gates that
  // route on auth.loading), but `/create-stack` renders the composer
  // immediately, so a direct entry there is the case. Users with a persisted
  // session skip it entirely — AuthContext starts `loading` false for them.
  const authResolving = Boolean(auth) && auth.loading;
  // The Medications control (STACK-565): logged-out visitors only, on the
  // landing and /create-stack composers, behind its kill switch. Never for a
  // signed-in visitor, and never on a composer that does not submit a stack.
  const showMedications = Boolean(config.FEATURES.GUEST_MEDICATION_CHECK)
    && showReducedGoals
    && ownSubmit
    && (surface === 'landing' || surface === 'app');
  // On the landing and /create-stack composers the goal box is a combobox of
  // the preset goals, for everyone (Aravind, 2026-10-09, STACK-565; it
  // replaced the Health goals button, which had replaced the chip rail and
  // "More goals"). Analyze keeps its rail.
  const comboboxPattern = ownSubmit && (surface === 'landing' || surface === 'app');
  const visibleGoals = showReducedGoals
    ? LOGGED_OUT_GOALS
    : compact
      ? CURATED_GOALS.slice(0, 4)
      : CURATED_GOALS;
  const menuGoals = compact ? [...CURATED_GOALS.slice(4), ...LONG_TAIL_GOALS] : LONG_TAIL_GOALS;
  const chipLabel = (goal) => (compact ? SHORT_LABELS[goal] || goal : goal);
  const inputRef = useRef(null);
  const cardRef = useRef(null);
  const inputFocusedRef = useRef(false);
  const focusTimeoutsRef = useRef([]);
  const viewportDebounceRef = useRef(null);
  const hasLoggedInputRef = useRef(false);

  // A pick fills the box with the exact preset label and never submits.
  const combo = useGoalCombobox({
    enabled: comboboxPattern && !loading,
    value: healthGoals,
    setValue: setHealthGoals,
    surface,
    compact,
    anchorRef: cardRef,
    inputRef,
    onPick: () => {
      hasLoggedInputRef.current = true;
      logAnalyticsEvent('composer_goal_selected', { surface, source: 'suggestion' });
    },
  });

  // Mobile browsers and native WebViews do not consistently pan a multiline
  // input above the software keyboard. Use the visual viewport when available
  // so the focused field stays centered in the actually visible area.
  const keepFocusedInputVisible = useCallback(() => {
    if (!compact || !inputFocusedRef.current || !inputRef.current) return;

    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportHeight = viewport?.height || window.innerHeight;
    const inputRect = inputRef.current.getBoundingClientRect();
    const visiblePadding = 16;
    const visibleTop = viewportTop + visiblePadding;
    const visibleBottom = viewportTop + viewportHeight - visiblePadding;

    if (inputRect.top >= visibleTop && inputRect.bottom <= visibleBottom) return;

    const centeredTop = viewportTop + Math.max(
      visiblePadding,
      (viewportHeight - inputRect.height) / 2
    );
    window.scrollBy({
      top: inputRect.top - centeredTop,
      behavior: 'smooth',
    });
  }, [compact]);

  useEffect(() => {
    if (!compact) return undefined;

    const handleViewportResize = () => {
      if (!inputFocusedRef.current) return;
      // Debounce: Android streams resize events through the whole keyboard
      // animation. Correcting per event issues overlapping smooth scrolls
      // computed against mid-animation heights — each cancelling the last and
      // fighting the browser's own scroll-into-view. Act once, after the
      // stream settles.
      if (viewportDebounceRef.current) clearTimeout(viewportDebounceRef.current);
      viewportDebounceRef.current = setTimeout(keepFocusedInputVisible, 120);
    };

    window.visualViewport?.addEventListener('resize', handleViewportResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
      focusTimeoutsRef.current.forEach(clearTimeout);
      focusTimeoutsRef.current = [];
      if (viewportDebounceRef.current) clearTimeout(viewportDebounceRef.current);
    };
  }, [compact, keepFocusedInputVisible]);

  // ── Rotate the example placeholder while the input is empty ─────────────────
  useEffect(() => {
    if (healthGoals.trim()) return;
    let fadeTimeout;
    const id = setInterval(() => {
      setPlaceholderVisible(false);
      fadeTimeout = setTimeout(() => {
        setExampleIndex(i => (i + 1) % EXAMPLE_GOALS.length);
        setPlaceholderVisible(true);
      }, 250);
    }, 2800);
    return () => {
      clearInterval(id);
      clearTimeout(fadeTimeout);
    };
  }, [healthGoals]);

  // ── Keyboard: submit on Enter (not Shift+Enter) ──────────────────────────────
  const handleKeyDown = (e) => {
    // With the suggestion list open, its keys (Up/Down/Enter/Escape) belong
    // to it: Enter picks a row or closes the list, and never submits.
    if (combo.onKeyDown(e)) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Without its own submit (Analyze), the page's footer is the one action,
      // which also needs a supplement list. Enter still keeps the goal to a
      // single line, but starts nothing.
      if (ownSubmit) handleSubmit();
    }
  };

  const handleSubmit = useCallback(() => {
    if (!healthGoals.trim() || loading) return;
    // Medications ride along only as a count: they stay in the tab store and
    // never join the stack request or analytics (docs/guest-medication-check.md §5).
    const medicationCount = showMedications ? getGuestMedications().length : 0;
    logAnalyticsEvent('composer_submit', {
      surface,
      health_goals: healthGoals,
      has_medications: medicationCount > 0,
      medication_count: medicationCount,
      has_preset_goal: Boolean(matchPreset(healthGoals)),
    });
    onSubmit?.();
  }, [healthGoals, loading, onSubmit, surface, showMedications]);

  const handleCancel = () => {
    logAnalyticsEvent('composer_cancel', { surface });
    onCancel?.();
  };

  // The chip rail (Analyze only, now): a chip fills the goal, and picking it
  // again clears it. No chip submits anywhere — only the arrow does (Aravind,
  // 2026-10-08, STACK-565; before that the landing and /create-stack chips
  // generated in one tap).
  const handleChipClick = (goal) => {
    hasLoggedInputRef.current = true;
    logAnalyticsEvent('composer_goal_chip_clicked', { goal, surface });
    setHealthGoals(healthGoals === goal ? '' : goal);
  };

  const handleMoreOpen = (e) => {
    setMoreAnchorEl(e.currentTarget);
    logAnalyticsEvent('composer_more_goals_opened', { surface });
  };

  const handleMoreClose = () => setMoreAnchorEl(null);

  const handleLongTailClick = (goal) => {
    handleChipClick(goal);
    handleMoreClose();
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setHealthGoals(v);
    if (v.trim() && !hasLoggedInputRef.current) {
      logAnalyticsEvent('input_health_goal', { surface });
      hasLoggedInputRef.current = true;
    }
    if (!v.trim()) hasLoggedInputRef.current = false;
    combo.onInputChange();
  };

  const handleInputFocus = () => {
    inputFocusedRef.current = true;
    setInputFocused(true);
    // Fallback ladder for environments without visualViewport resize events
    // (older WebViews). Keyboard animations take 300-500ms+, so a single
    // early check can measure pre-keyboard geometry, conclude the input is
    // visible, and never run again — retry across the animation window.
    focusTimeoutsRef.current.forEach(clearTimeout);
    focusTimeoutsRef.current = [250, 500, 850].map((ms) => setTimeout(keepFocusedInputVisible, ms));
    combo.onFocus();
  };

  const handleInputBlur = () => {
    inputFocusedRef.current = false;
    setInputFocused(false);
    focusTimeoutsRef.current.forEach(clearTimeout);
    focusTimeoutsRef.current = [];
    combo.onBlur();
  };

  // The text stays mounted through the swap and only its opacity animates —
  // blanking it instead made the box read as dead space for a quarter second
  // on every rotation, and the ::placeholder opacity transition never ran.
  const placeholder = `e.g. "${EXAMPLE_GOALS[exampleIndex]}"`;

  // Submit is "armed" only when there's something to send — drives the button affordance.
  const inputEmpty = !healthGoals.trim();

  const submitButton = (
    <IconButton
      onClick={loading ? handleCancel : handleSubmit}
      size="small"
      aria-label={loading ? 'Stop generation' : submitLabel}
      sx={{
        background: loading ? alpha(LC.ink, 0.65) : T.submitBg,
        // The arrow and stop glyphs are graphics, not text: white on the
        // accent measures 3.5:1, which clears the 3:1 graphical bar the
        // way it would not clear AA for body copy.
        color: 'primary.contrastText',
        // Phone: ≥44px tap target (STACK-565 sighted pass). Desktop keeps 40px.
        width: { xs: r(44), sm: '2.5rem' },
        height: { xs: r(44), sm: '2.5rem' },
        borderRadius: '50%',
        flexShrink: 0,
          // Raised affordance once armed; flat + dimmed while there's nothing to send.
        boxShadow: !loading && inputEmpty ? 'none' : T.submitShadow,
        opacity: !loading && inputEmpty ? 0.65 : 1,
        cursor: !loading && inputEmpty ? 'default' : 'pointer',
        '&:hover': {
          background: loading
            ? 'rgba(10,22,40,0.80)'
            : inputEmpty
              ? T.submitBg
              : T.submitHover,
        },
        transition: 'background 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease',
        '&:active': { transform: inputEmpty && !loading ? 'none' : 'scale(0.92)' },
      }}
    >
      {loading ? (
        <StopIcon sx={{ fontSize: '0.875rem' }} />
      ) : (
        <ArrowForwardRoundedIcon sx={{ fontSize: '1rem' }} />
      )}
    </IconButton>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Headline + sub ─────────────────────────────────────────────────── */}
      {showHeader && (
        <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.625rem', sm: '2rem', md: '2.25rem' },
              lineHeight: 1.2,
              color: T.ink,
              mb: 0.75,
            }}
          >
            What's your health goal?
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              lineHeight: 1.55,
              color: T.muted,
              maxWidth: '26rem',
              mx: 'auto',
            }}
          >
            Evidence-informed support, personalized to you.
          </Typography>
        </Box>
      )}

      {/* ── Compact instruction shown when the box has no heading of its own
             (landing hero owns the headline; /create-stack opts out of the
             large heading in favor of this) ──────────────────────────────── */}
      {/* Landing renders this prompt on the hero gradient instead (the card
          itself stays chrome-free, Perplexity-style). */}
      {!showHeader && surface !== 'landing' && (
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            fontWeight: 600,
            color: LC.evergreen,
            mb: 0.5,
            pl: 0.25,
          }}
        >
          Type your goal, or pick a common one.
        </Typography>
      )}

      {/* ── Composer card ──────────────────────────────────────────────────── */}
      {/* One Perplexity-style container: input on top, a single bottom row
          with chips left + submit pinned right. On the landing it IS the hero
          card — borderless (modern chat composers carry no border), elevation
          from the deep hero shadow + contrast against the gradient. On `app`
          (/create-stack) it stands alone on a light page bg, so it keeps a
          hairline + softer shadow for separation. */}
      <Box
        ref={cardRef}
        // Composer popovers scroll this card near the top on a short window.
        data-composer-card=""
        sx={{
          backgroundColor: T.surface,
          border: surface === 'landing' ? 'none' : `0.0625rem solid ${T.stroke}`,
          borderRadius: surface === 'landing' ? '1.125rem' : '1rem',
          p: surface === 'landing' ? { xs: 1, sm: 1.25 } : { xs: 1.5, sm: 1.75 },
          boxShadow: surface === 'landing'
            ? '0 0.125rem 0.375rem rgba(4,18,8,.25), 0 1.875rem 4rem -1rem rgba(4,18,8,.55)'
            : '0 0.625rem 1.75rem rgba(10,22,40,0.18), 0 0.125rem 0.5rem rgba(10,22,40,0.10)',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:focus-within': surface === 'landing' ? {} : {
            borderColor: T.strokeFocus,
            boxShadow: `0 0.625rem 1.75rem rgba(10,22,40,0.20), 0 0 0 0.1875rem rgba(27,79,114,0.12)`,
          },
        }}
      >
        {/* ── Text input ───────────────────────────────────────────────────── */}
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          placeholder={placeholder}
          value={healthGoals}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={loading}
          variant="standard"
          inputProps={{ 'aria-label': 'Describe your health goal', ...combo.inputAttributes }}
          InputProps={{
            disableUnderline: true,
            endAdornment: <GoalChevron combo={combo} disabled={loading} />,
            // Resting caret: without it an empty box reads as blank space
            // rather than something you can type into. Stays mounted (only
            // visibility toggles) so the placeholder never shifts sideways
            // when the real caret takes over on focus.
            startAdornment: (
              <Box
                aria-hidden
                sx={{
                  flex: '0 0 auto',
                  width: '0.09375rem',
                  height: '1.15rem',
                  mr: 0.375,
                  backgroundColor: T.ink,
                  visibility: !healthGoals.trim() && !inputFocused && !loading
                    ? 'visible'
                    : 'hidden',
                  animation: 'composerCaretBlink 1.1s step-end infinite',
                  '@keyframes composerCaretBlink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0 },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              />
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              // 16px floor everywhere: iOS zooms the whole page when focusing
              // any input below 16px — the primary "keyboard covers the box"
              // mechanism on iPhones. Never go below 1rem here.
              fontSize: '1rem',
              color: T.ink,
              lineHeight: 1.6,
              minHeight: { xs: '4rem', sm: 'auto' },
              px: 0.75,
              py: { xs: 0.75, sm: 0.5 },
              alignItems: 'center',
            },
            '& .MuiInputBase-input': {
              '&::placeholder': {
                color: T.finePrint,
                opacity: placeholderVisible ? 1 : 0,
                fontStyle: 'italic',
                transition: 'opacity 0.25s ease',
              },
            },
          }}
        />

        {/* ── Bottom control row ──────────────────────────────────────────
            Landing and /create-stack (Aravind, 2026-10-09, STACK-565):
            Medications for a logged-out visitor, with the arrow pinned right —
            the only thing that submits. The goal itself is the field above.
            Analyze keeps the chip rail. */}
        {comboboxPattern ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: r(8), mt: r(8) }}>
            {showMedications && (
              // Hidden, not removed, while generating, so the stop button
              // keeps its place.
              <Box sx={{ display: 'flex', flex: 'none', visibility: loading ? 'hidden' : 'visible' }}>
                <GuestMedicationsControl surface={surface} />
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            {submitButton}
          </Box>
        ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: r(8), mt: r(8) }}>
          <Box
            data-testid="composer-goal-rail"
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              // Hidden (not collapsed) while generating so the stop button
              // keeps a stable position bottom-right, and for the same reason
              // while auth resolves: the row holds its height instead of
              // showing a rail built for the other audience.
              visibility: loading || authResolving ? 'hidden' : 'visible',
            }}
          >
            {visibleGoals.map((goal) => {
              const selected = healthGoals === goal;
              return (
                <Chip
                  key={goal}
                  label={chipLabel(goal)}
                  size="small"
                  onClick={() => handleChipClick(goal)}
                  // Where a chip toggles the goal instead of submitting it,
                  // say whether it is on: a second press clears the goal, and
                  // nothing else announces that.
                  aria-pressed={ownSubmit ? undefined : selected}
                  sx={{
                    height: { xs: '2.25rem', sm: '1.625rem' },
                    fontSize: { xs: '0.8125rem', sm: '0.75rem' },
                    fontWeight: selected ? 600 : 400,
                    letterSpacing: 0,
                    borderRadius: '0.5rem',
                    border: `0.0625rem solid ${selected ? 'transparent' : T.chipBorder}`,
                    backgroundColor: selected ? T.chipSelected : T.chipIdle,
                    color: selected ? T.chipSelectedInk : LC.evergreen,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selected ? T.chipSelected : T.chipHover,
                      borderColor: selected ? 'transparent' : LC.accent,
                    },
                    '& .MuiChip-label': { px: 1 },
                    transition: 'background-color 0.12s ease, border-color 0.12s ease',
                  }}
                />
              );
            })}

            {/* "More goals" expander — never rendered for logged-out
                visitors, so it is unreachable by keyboard and screen reader
                rather than merely hidden. */}
            {!showReducedGoals && (
              <>
              <Chip
                label="More goals"
                size="small"
                icon={<ExpandMoreIcon sx={{ fontSize: '0.875rem !important', ml: '0.375rem !important' }} />}
                onClick={handleMoreOpen}
                sx={{
                  height: { xs: '2.25rem', sm: '1.625rem' },
                  fontSize: { xs: '0.8125rem', sm: '0.75rem' },
                  fontWeight: 400,
                  borderRadius: '0.5rem',
                  border: `0.0625rem solid ${T.chipBorder}`,
                  backgroundColor: T.chipIdle,
                  color: LC.evergreen,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: T.chipHover,
                    borderColor: LC.accent,
                  },
                  '& .MuiChip-label': { px: 0.75, pr: 1 },
                }}
              />

              <Menu
                anchorEl={moreAnchorEl}
                open={Boolean(moreAnchorEl)}
                onClose={handleMoreClose}
                PaperProps={{
                  sx: {
                    backgroundColor: LC.surfaceAlt,
                    border: `0.0625rem solid ${T.stroke}`,
                    boxShadow: '0 0.5rem 1.5rem rgba(10,22,40,0.12)',
                    borderRadius: '0.75rem',
                    minWidth: '11rem',
                    mt: 0.5,
                  },
                }}
              >
                {menuGoals.map((goal) => (
                  <MenuItem
                    key={goal}
                    onClick={() => handleLongTailClick(goal)}
                    sx={{
                      fontSize: '0.8125rem',
                      color: T.ink,
                      py: 0.75,
                      px: 1.75,
                      '&:hover': {
                        backgroundColor: T.chipHover,
                      },
                      '&.Mui-selected': {
                        backgroundColor: T.chipSelected,
                      },
                    }}
                    selected={healthGoals === goal}
                  >
                    {goal}
                  </MenuItem>
                ))}
              </Menu>
              </>
            )}
          </Box>

          {/* No arrow when the box has no submit of its own (Analyze). */}
          {ownSubmit && submitButton}
        </Box>
        )}
      </Box>

      <GoalSuggestions combo={combo} anchorRef={cardRef} onSheetInputChange={(v) => handleInputChange({ target: { value: v } })} />

      {/* ── Fine-print disclaimer (app surface only; the landing renders its
             "2 free stacks" microcopy on the hero gradient instead) ─────────── */}
      {surface !== 'landing' && (
        <Box sx={{
          mt: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
        }}>
          <InfoOutlinedIcon sx={{
            fontSize: '1rem',
            mr: 0.75,
            color: LC.evergreen,
            mt: '0.0625rem',
            flexShrink: 0,
          }} />
          <Typography sx={{
            fontSize: '0.75rem',
            color: LC.inkSoft,
            lineHeight: 1.4,
            fontWeight: 500,
          }}>
            This product is not intended to diagnose, treat, cure, or prevent any disease. Please consult your healthcare provider before taking any supplements.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
